import type { NextPage } from 'next';
import Head from 'next/head';
import Layout from '@/components/Layout';
import ClientUploadFrequencyWidget from '@/components/ClientUploadFrequencyWidget';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { getActividadReciente } from '@/lib/supabaseService';
import Link from 'next/link';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableFooter } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

/* ─── Types ──────────────────────────────────────────── */

interface DashboardMetrics {
  documentosPendientes: number;
  iaProcesamientosMes: number;
  incidenciasAbiertas: number;
  clientesActivos: number;
  respuestasPendientes: number;
}

interface ClienteSummary {
  clienteId: string;
  nombre: string;
  cif: string;
  todos: number;
  contabilizados: number;
  errores: number;
  borrados: number;
  enProceso: number;
  pendienteRevision: number;
  contabilizando: number;
}

/* ─── Count-up animation hook ────────────────────────── */

function useCountUp(target: number, duration = 900): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (target <= 0) {
      setValue(0);
      return;
    }

    let frame: number;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return value;
}

/* ─── Component ──────────────────────────────────────── */

const AsesorDashboard: NextPage = () => {
  const { user, loading } = useAuth();
  const router = useRouter();

  /* ── Existing state ── */
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    documentosPendientes: 0,
    iaProcesamientosMes: 0,
    incidenciasAbiertas: 0,
    clientesActivos: 0,
    respuestasPendientes: 0,
  });
  const [assignedClientsData, setAssignedClientsData] = useState<
    Array<{ id?: string; nombre_comercial?: string | null; razon_social?: string | null; nif?: string | null }>
  >([]);
  const [loadingDatos, setLoadingDatos] = useState(true);

  /* ── Summary table state ── */
  const [summaryData, setSummaryData] = useState<ClienteSummary[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'todos' | 'nombre' | 'cif'>('todos');
  const [ocultarEliminadas, setOcultarEliminadas] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  /* ── Count-up animated values (must be before early return) ── */
  const animDocs = useCountUp(metrics.documentosPendientes);
  const animInc = useCountUp(metrics.incidenciasAbiertas);
  const animClientes = useCountUp(metrics.clientesActivos);
  const animResp = useCountUp(metrics.respuestasPendientes);

  /* ── Data loading ── */
  const loadDashboardData = useCallback(async () => {
    try {
      if (!user?.id) return;

      const { data: assignedClients, error: clientError } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(*)')
        .eq('asesor_id', user.id);

      if (clientError) console.error('Error loading assigned clients:', clientError);

      const clientIds: string[] = assignedClients?.map((ac) => ac.cliente_id) || [];

      let documentosPendientes = 0;
      let incidenciasAbiertas = 0;
      let respuestasPendientes = 0;

      if (clientIds.length > 0) {
        const { data: pendingDocs } = await supabase
          .from('documentos')
          .select('id')
          .in('cliente_id', clientIds)
          .in('estado', ['pendiente', 'procesado_ia']);
        documentosPendientes = pendingDocs?.length || 0;

        const { data: openIncidents } = await supabase
          .from('incidencias')
          .select('*')
          .in('cliente_id', clientIds)
          .in('estado', ['nueva', 'en_revision']);
        incidenciasAbiertas = openIncidents?.length || 0;
        respuestasPendientes = openIncidents?.filter((i) =>
          i.estado === 'nueva' || !i.fecha_ultima_respuesta
        ).length || 0;

        /* ── Summary table data: query all docs for status counts ── */
        const { data: allDocs } = await supabase
          .from('documentos')
          .select('cliente_id, estado')
          .in('cliente_id', clientIds);

        const countsByClient = new Map<string, Record<string, number>>();
        if (allDocs) {
          for (const doc of allDocs) {
            const cid = String(doc.cliente_id);
            if (!countsByClient.has(cid)) countsByClient.set(cid, {});
            const counts = countsByClient.get(cid)!;
            const estado = String(doc.estado);
            counts[estado] = (counts[estado] || 0) + 1;
          }
        }

        const newSummary: ClienteSummary[] = (assignedClients || [])
          .filter((ac) => ac.clientes)
          .map((ac) => {
            const c = ac.clientes[0];
            const cid = String(ac.cliente_id);
            const counts = countsByClient.get(cid) || {};

            const contabilizados = counts['contabilizado'] || 0;
            const incidencias = counts['incidencia'] || 0;
            const rechazados = counts['rechazado'] || 0;
            const procesadoIA = counts['procesado_ia'] || 0;
            const pendientes = counts['pendiente'] || 0;
            const validados = counts['validado'] || 0;

            return {
              clienteId: cid,
              nombre: String(c?.nombre_comercial || c?.razon_social || 'Sin nombre'),
              cif: String(c?.nif || ''),
              todos: contabilizados + incidencias + rechazados + procesadoIA + pendientes + validados,
              contabilizados,
              errores: incidencias,
              borrados: rechazados,
              enProceso: procesadoIA,
              pendienteRevision: pendientes,
              contabilizando: validados,
            };
          });

        setSummaryData(newSummary);
      }

      setMetrics({
        documentosPendientes,
        iaProcesamientosMes: 0,
        incidenciasAbiertas,
        clientesActivos: assignedClients?.length || 0,
        respuestasPendientes,
      });

      setAssignedClientsData(
        (assignedClients || [])
          .map((ac) => ac.clientes[0])
          .filter(Boolean)
      );
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoadingDatos(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user && user.rol === 'asesor') {
      loadDashboardData();
    }
  }, [user, loading, router, loadDashboardData]);

  /* ── Derived summary state ── */
  const filteredSummary = useMemo(() => {
    let data = summaryData.map((row) => {
      if (ocultarEliminadas) {
        return { ...row, todos: row.todos - row.borrados, borrados: 0 };
      }
      return row;
    });

    if (ocultarEliminadas) {
      data = data.filter((row) => row.todos > 0);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      data = data.filter((row) => {
        if (searchField === 'nombre') return row.nombre.toLowerCase().includes(term);
        if (searchField === 'cif') return row.cif.toLowerCase().includes(term);
        return row.nombre.toLowerCase().includes(term) || row.cif.toLowerCase().includes(term);
      });
    }

    return data;
  }, [summaryData, searchTerm, searchField, ocultarEliminadas]);

  const totals = useMemo(() => ({
    todos: filteredSummary.reduce((s, r) => s + r.todos, 0),
    contabilizados: filteredSummary.reduce((s, r) => s + r.contabilizados, 0),
    errores: filteredSummary.reduce((s, r) => s + r.errores, 0),
    borrados: filteredSummary.reduce((s, r) => s + r.borrados, 0),
    enProceso: filteredSummary.reduce((s, r) => s + r.enProceso, 0),
    pendienteRevision: filteredSummary.reduce((s, r) => s + r.pendienteRevision, 0),
    contabilizando: filteredSummary.reduce((s, r) => s + r.contabilizando, 0),
  }), [filteredSummary]);

  const totalPages = Math.max(1, Math.ceil(filteredSummary.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredSummary.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  /* ── Metric trends (% of total docs in each state) ── */
  const metricTrends = useMemo(() => {
    const totalDocs = summaryData.reduce((s, r) => s + r.todos, 0);
    if (totalDocs === 0) return { docs: undefined, inc: undefined, clientes: undefined, resp: undefined };

    const docsRate = Math.round((metrics.documentosPendientes / totalDocs) * 100);
    const errorRate = Math.round((metrics.incidenciasAbiertas / Math.max(totalDocs, 1)) * 100);
    const respRate = metrics.incidenciasAbiertas > 0
      ? Math.round((metrics.respuestasPendientes / metrics.incidenciasAbiertas) * 100)
      : 0;

    return {
      docs: docsRate > 0 ? { valor: docsRate, positivo: false } : undefined,
      inc: errorRate > 0 ? { valor: errorRate, positivo: false } : undefined,
      clientes: metrics.clientesActivos > 0 ? { valor: metrics.clientesActivos, positivo: true } : undefined,
      resp: respRate > 0 ? { valor: respRate, positivo: false } : undefined,
    };
  }, [metrics, summaryData]);

  /* ── CSV Export ── */
  const exportCSV = useCallback(() => {
    const headers = ['Cliente', 'CIF', 'Todos', 'Contabilizados', 'Errores', 'Borrados', 'En proceso', 'Pdte. Revisión', 'Contabilizando'];

    const rows = filteredSummary.map((row) =>
      [
        `"${row.nombre.replace(/"/g, '""')}"`,
        `"${row.cif}"`,
        row.todos,
        row.contabilizados,
        row.errores,
        row.borrados,
        row.enProceso,
        row.pendienteRevision,
        row.contabilizando,
      ].join(',')
    );

    rows.push(
      [
        '"Total"', '""',
        totals.todos, totals.contabilizados, totals.errores, totals.borrados,
        totals.enProceso, totals.pendienteRevision, totals.contabilizando,
      ].join(',')
    );

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `resumen_contabilidad_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('CSV descargado correctamente');
  }, [filteredSummary, totals]);

  /* ── Loading ── */
  if (loading || !user || user.rol !== 'asesor' || loadingDatos) {
    return (
      <div className="min-h-screen bg-calm-bg">
        <div className="px-12 pb-12 pt-8 space-y-12">
          <div className="space-y-3">
            <Skeleton className="h-12 w-72" />
            <Skeleton className="h-4 w-80" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-40 rounded-huge" />
            ))}
          </div>
          <Skeleton className="h-20 rounded-huge" />
          <Skeleton className="h-[400px] rounded-huge" />
        </div>
      </div>
    );
  }

  /* ── Status cell color helper ── */
  const cellColor = (count: number, type: 'success' | 'error' | 'blue' | 'neutral') => {
    if (count === 0) return 'text-calm-accent';
    switch (type) {
      case 'success': return 'text-emerald-600';
      case 'error': return 'text-red-500';
      case 'blue': return 'text-blue-600';
      default: return 'text-calm-text';
    }
  };

  return (
    <>
      <Head>
        <title>Panel de Control — Externaliza2</title>
        <link rel="icon" href="/logo.png" />
      </Head>
      <Layout rol="asesor">
        <div className="h-screen overflow-y-auto scrollbar-thin">
          <div className="px-12 pb-12 space-y-12">

            {/* ──────── Header ──────── */}
            <div>
              <h1 className="text-5xl font-display font-light text-calm-text">Panel de Control</h1>
              <p className="text-calm-muted font-light mt-2">
                Hola, {user.nombre?.split(' ')[0]}. Tu resumen de actividad.
              </p>
            </div>

            {/* ──────── Metric Cards ──────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8">

              {/* Docs Pendientes */}
              <Link href="/asesor/documentos-pendientes" className="block group">
                <div className="bg-gradient-to-br from-fresh-sky-50 to-fresh-sky-50/30 border border-fresh-sky-200/60 rounded-huge p-8 hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
                  <div className="flex items-start justify-between">
                    <div className="space-y-3">
                      <p className="text-[10px] uppercase tracking-widest text-calm-muted">Docs Pendientes</p>
                      <p className="text-2xl font-light text-calm-text">{animDocs.toLocaleString('es-ES')}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-light text-calm-muted">Requieren revisión</p>
                        {metricTrends.docs && (
                          <span className={cn('text-xs font-light flex items-center gap-0.5', metricTrends.docs.positivo ? 'text-emerald-600' : 'text-red-500')}>
                            <span className="material-symbols-outlined text-[14px]">{metricTrends.docs.positivo ? 'trending_up' : 'trending_down'}</span>
                            {Math.abs(metricTrends.docs.valor)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-fresh-sky-100 flex items-center justify-center text-fresh-sky-600">
                      <span className="material-symbols-outlined text-xl">description</span>
                    </div>
                  </div>
                </div>
              </Link>

              {/* Incidencias */}
              <Link href="/asesor/incidencias" className="block group">
                <div className="bg-gradient-to-br from-amber-50 to-amber-50/30 border border-amber-200/60 rounded-huge p-8 hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
                  <div className="flex items-start justify-between">
                    <div className="space-y-3">
                      <p className="text-[10px] uppercase tracking-widest text-calm-muted">Incidencias</p>
                      <p className="text-2xl font-light text-calm-text">{animInc.toLocaleString('es-ES')}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-light text-calm-muted">
                          {metrics.incidenciasAbiertas === 1 ? '1 pendiente' : `${metrics.incidenciasAbiertas} pendientes`}
                        </p>
                        {metricTrends.inc && (
                          <span className={cn('text-xs font-light flex items-center gap-0.5', metricTrends.inc.positivo ? 'text-emerald-600' : 'text-red-500')}>
                            <span className="material-symbols-outlined text-[14px]">{metricTrends.inc.positivo ? 'trending_up' : 'trending_down'}</span>
                            {Math.abs(metricTrends.inc.valor)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                      <span className="material-symbols-outlined text-xl">warning</span>
                    </div>
                  </div>
                </div>
              </Link>

              {/* Clientes */}
              <Link href="/asesor/clientes" className="block group">
                <div className="bg-gradient-to-br from-steel-azure-50 to-steel-azure-50/30 border border-steel-azure-200/60 rounded-huge p-8 hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
                  <div className="flex items-start justify-between">
                    <div className="space-y-3">
                      <p className="text-[10px] uppercase tracking-widest text-calm-muted">Clientes</p>
                      <p className="text-2xl font-light text-calm-text">{animClientes.toLocaleString('es-ES')}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-light text-calm-muted">Asignados a ti</p>
                        {metricTrends.clientes && (
                          <span className={cn('text-xs font-light flex items-center gap-0.5', metricTrends.clientes.positivo ? 'text-emerald-600' : 'text-red-500')}>
                            <span className="material-symbols-outlined text-[14px]">{metricTrends.clientes.positivo ? 'trending_up' : 'trending_down'}</span>
                            {Math.abs(metricTrends.clientes.valor)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-steel-azure-100 flex items-center justify-center text-steel-azure-600">
                      <span className="material-symbols-outlined text-xl">group</span>
                    </div>
                  </div>
                </div>
              </Link>

              {/* Respuestas */}
              <Link href="/asesor/incidencias" className="block group">
                <div className="bg-gradient-to-br from-rose-50 to-rose-50/30 border border-rose-200/60 rounded-huge p-8 hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
                  <div className="flex items-start justify-between">
                    <div className="space-y-3">
                      <p className="text-[10px] uppercase tracking-widest text-calm-muted">Respuestas</p>
                      <p className="text-2xl font-light text-calm-text">{animResp.toLocaleString('es-ES')}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-light text-calm-muted">Requieren atención</p>
                        {metricTrends.resp && (
                          <span className={cn('text-xs font-light flex items-center gap-0.5', metricTrends.resp.positivo ? 'text-emerald-600' : 'text-red-500')}>
                            <span className="material-symbols-outlined text-[14px]">{metricTrends.resp.positivo ? 'trending_up' : 'trending_down'}</span>
                            {Math.abs(metricTrends.resp.valor)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600">
                      <span className="material-symbols-outlined text-xl">chat_bubble</span>
                    </div>
                  </div>
                </div>
              </Link>

            </div>

            {/* ──────── Client Upload Frequency KPI ──────── */}
            <ClientUploadFrequencyWidget asesorId={user?.id || ''} />

            {/* ════════════════════════════════════════════════════════════
                  CUADRO RESUMEN CONTABILIDAD
               ════════════════════════════════════════════════════════════ */}
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 transition-all">

              {/* Card Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-steel-azure-500">bar_chart</span>
                  <h2 className="text-lg font-display font-light text-calm-text">Resumen de Contabilidad</h2>
                </div>
                <button
                  onClick={exportCSV}
                  className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-all flex items-center gap-2"
                >
                  <span className="material-symbols-outlined text-base">download</span>
                  Descargar CSV
                </button>
              </div>

              <div className="space-y-6">
                {/* ── Filters Bar ── */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  {/* Search */}
                  <div className="relative flex-1 max-w-sm">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-calm-accent pointer-events-none text-lg">search</span>
                    <Input
                      type="text"
                      placeholder="Buscar cliente…"
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      className="pl-10 font-light"
                    />
                  </div>

                  {/* Campo filter */}
                  <Select value={searchField} onValueChange={(v) => { setSearchField(v as 'todos' | 'nombre' | 'cif'); setCurrentPage(1); }}>
                    <SelectTrigger className="w-[180px] font-light">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los campos</SelectItem>
                      <SelectItem value="nombre">Nombre</SelectItem>
                      <SelectItem value="cif">CIF</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Toggle: Ocultar eliminadas */}
                  <div className="flex items-center gap-2.5 ml-auto">
                    <Label htmlFor="ocultar-eliminadas" className="text-sm font-light text-calm-muted whitespace-nowrap cursor-pointer">
                      Ocultar eliminadas
                    </Label>
                    <Switch
                      id="ocultar-eliminadas"
                      checked={ocultarEliminadas}
                      onCheckedChange={(checked) => { setOcultarEliminadas(checked); setCurrentPage(1); }}
                    />
                  </div>
                </div>

                <Separator className="bg-calm-border" />

                {/* ── Summary Table ── */}
                <div className="rounded-xl border border-calm-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="text-left px-4 py-3 text-[10px] uppercase tracking-widest font-light text-calm-muted whitespace-nowrap">
                          Cliente
                        </TableHead>
                        <TableHead className="text-center px-3 py-3 text-[10px] uppercase tracking-widest font-light text-calm-muted whitespace-nowrap">
                          Todos
                        </TableHead>
                        <TableHead className="text-center px-3 py-3 text-[10px] uppercase tracking-widest font-light text-calm-muted whitespace-nowrap">
                          Contabilizados
                        </TableHead>
                        <TableHead className="text-center px-3 py-3 text-[10px] uppercase tracking-widest font-light text-calm-muted whitespace-nowrap">
                          Errores
                        </TableHead>
                        <TableHead className="text-center px-3 py-3 text-[10px] uppercase tracking-widest font-light text-calm-muted whitespace-nowrap">
                          Borrados
                        </TableHead>
                        <TableHead className="text-center px-3 py-3 text-[10px] uppercase tracking-widest font-light text-calm-muted whitespace-nowrap">
                          En proceso
                        </TableHead>
                        <TableHead className="text-center px-3 py-3 text-[10px] uppercase tracking-widest font-light text-calm-muted whitespace-nowrap">
                          Pdte. Revisión
                        </TableHead>
                        <TableHead className="text-center px-3 py-3 text-[10px] uppercase tracking-widest font-light text-calm-muted whitespace-nowrap">
                          Contabilizando
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="px-4 py-12 text-center text-calm-accent">
                            <div className="flex flex-col items-center gap-2">
                              <span className="material-symbols-outlined text-3xl text-calm-accent">bar_chart</span>
                              <span className="font-light">No se encontraron datos de contabilidad</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedData.map((row, i) => (
                          <TableRow
                            key={row.clienteId}
                            className={cn(
                              'transition-colors hover:bg-slate-50/60',
                              i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'
                            )}
                          >
                            <TableCell className="px-4 py-3 text-calm-text font-light max-w-[220px] truncate" title={row.nombre}>
                              <div>
                                <div className="truncate">{row.nombre}</div>
                                {row.cif && (
                                  <div className="text-xs text-calm-accent font-light">{row.cif}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className={cn('text-center px-3 py-3 tabular-nums font-light', cellColor(row.todos, 'neutral'))}>
                              {row.todos}
                            </TableCell>
                            <TableCell className={cn('text-center px-3 py-3 tabular-nums font-light', cellColor(row.contabilizados, 'success'))}>
                              {row.contabilizados}
                            </TableCell>
                            <TableCell className={cn('text-center px-3 py-3 tabular-nums font-light', cellColor(row.errores, 'error'))}>
                              {row.errores}
                            </TableCell>
                            <TableCell className={cn('text-center px-3 py-3 tabular-nums font-light', cellColor(row.borrados, 'neutral'))}>
                              {row.borrados}
                            </TableCell>
                            <TableCell className={cn('text-center px-3 py-3 tabular-nums font-light', cellColor(row.enProceso, 'blue'))}>
                              {row.enProceso}
                            </TableCell>
                            <TableCell className={cn('text-center px-3 py-3 tabular-nums font-light', cellColor(row.pendienteRevision, 'blue'))}>
                              {row.pendienteRevision}
                            </TableCell>
                            <TableCell className={cn('text-center px-3 py-3 tabular-nums font-light', cellColor(row.contabilizando, 'blue'))}>
                              {row.contabilizando}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                    {filteredSummary.length > 0 && (
                      <TableFooter>
                        <TableRow className="bg-slate-50 border-t border-calm-border">
                          <TableCell className="px-4 py-3 font-light text-calm-text">Total</TableCell>
                          <TableCell className="text-center px-3 py-3 font-light text-calm-text tabular-nums">{totals.todos}</TableCell>
                          <TableCell className="text-center px-3 py-3 font-light text-emerald-600 tabular-nums">{totals.contabilizados}</TableCell>
                          <TableCell className="text-center px-3 py-3 font-light text-red-500 tabular-nums">{totals.errores}</TableCell>
                          <TableCell className="text-center px-3 py-3 font-light text-calm-text tabular-nums">{totals.borrados}</TableCell>
                          <TableCell className="text-center px-3 py-3 font-light text-calm-text tabular-nums">{totals.enProceso}</TableCell>
                          <TableCell className="text-center px-3 py-3 font-light text-calm-text tabular-nums">{totals.pendienteRevision}</TableCell>
                          <TableCell className="text-center px-3 py-3 font-light text-calm-text tabular-nums">{totals.contabilizando}</TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </div>

                {/* ── Pagination ── */}
                <div className="flex flex-col sm:flex-row items-center justify-end gap-4 sm:gap-6 text-sm font-light text-calm-muted">
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap text-sm font-light text-calm-muted">Clientes por página:</Label>
                    <Select value={String(itemsPerPage)} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                      <SelectTrigger className="w-[70px] h-8 font-light">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="tabular-nums whitespace-nowrap font-light">
                    {filteredSummary.length === 0
                      ? '0 de 0'
                      : `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredSummary.length)} de ${filteredSummary.length}`
                    }
                  </span>
                  <div className="flex gap-1">
                    <button
                      className="border border-calm-border rounded-huge w-8 h-8 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all disabled:opacity-40"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      aria-label="Página anterior"
                    >
                      <span className="material-symbols-outlined text-base">chevron_left</span>
                    </button>
                    <button
                      className="border border-calm-border rounded-huge w-8 h-8 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all disabled:opacity-40"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      aria-label="Página siguiente"
                    >
                      <span className="material-symbols-outlined text-base">chevron_right</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ──────── Acceso Rápido a Clientes ──────── */}
            {assignedClientsData.length > 0 && (
              <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 transition-all">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-fresh-sky-500">apartment</span>
                    <h2 className="text-lg font-display font-light text-calm-text">Acceso Rápido</h2>
                  </div>
                  <Link
                    href="/asesor/clientes"
                    className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-all"
                  >
                    Ver todos ({metrics.clientesActivos})
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {assignedClientsData.slice(0, 6).map((cliente) => (
                    <Link
                      key={cliente.id}
                      href={`/asesor/clientes-detailed?cliente=${cliente.id}`}
                      className="group flex items-center gap-4 p-4 rounded-xl border border-fresh-sky-100/60 bg-white hover:bg-gradient-to-br hover:from-fresh-sky-50/40 hover:to-white hover:shadow-sm transition-all"
                    >
                      <div className="w-10 h-10 rounded-xl bg-fresh-sky-100 flex items-center justify-center text-fresh-sky-600 text-sm font-light flex-shrink-0">
                        {(cliente.nombre_comercial || cliente.razon_social)?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-light text-calm-text truncate">
                          {cliente.nombre_comercial || cliente.razon_social}
                        </p>
                        <p className="text-xs font-light text-calm-accent">{cliente.nif}</p>
                      </div>
                      <span className="material-symbols-outlined text-base text-calm-accent group-hover:text-calm-text transition-colors flex-shrink-0">chevron_right</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </Layout>
    </>
  );
};

export default AsesorDashboard;
